import { useState, useEffect, useCallback } from 'react';
import { ClientEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import {
  getRoomPushRuleState,
  setRoomPushRuleState,
  PushRuleState,
} from '../utils/pushRuleState';
import logger from '../utils/logger';

/**
 * React hook for managing Matrix push rules with real-time synchronization.
 *
 * This hook provides:
 * - Access to the current push rules state
 * - A function to get the notification state for a specific room
 * - A function to update the notification state for a specific room
 *
 * The hook automatically subscribes to Matrix account data changes
 * (m.push_rules) and updates the local state when push rules change
 * on the server, ensuring the UI always reflects the latest state.
 *
 * @example
 * ```jsx
 * function NotificationSettings({ roomId }) {
 *   const { getRoomState, setRoomState, pushRules } = usePushRules();
 *
 *   const currentState = getRoomState(roomId);
 *
 *   const handleMute = async () => {
 *     await setRoomState(roomId, PushRuleState.DONT_NOTIFY);
 *   };
 *
 *   return (
 *     <button onClick={handleMute}>
 *       {currentState === PushRuleState.DONT_NOTIFY ? 'Unmute' : 'Mute'}
 *     </button>
 *   );
 * }
 * ```
 *
 * @returns {{
 *   pushRules: object | null,
 *   getRoomState: (roomId: string) => string,
 *   setRoomState: (roomId: string, newState: string) => Promise<string>
 * }} Object containing:
 *   - pushRules: The raw Matrix push rules object (null if not yet loaded)
 *   - getRoomState: Function to get the current PushRuleState for a room
 *   - setRoomState: Async function to set the PushRuleState for a room
 */
export function usePushRules() {
  const client = useMatrixClient();
  const [pushRules, setPushRules] = useState(null);

  // Initialize push rules state and subscribe to changes
  useEffect(() => {
    if (!client) {
      setPushRules(null);
      return;
    }

    // Get initial push rules from client
    const initialRules = client.pushRules;
    setPushRules(initialRules);
    logger.debug('[usePushRules] Initialized with push rules:', {
      hasRules: !!initialRules,
      globalRuleTypes: initialRules?.global ? Object.keys(initialRules.global) : [],
    });

    /**
     * Handler for account data changes
     * Updates local state when push rules change on the server
     */
    const handleAccountData = (event) => {
      if (event.getType() === 'm.push_rules') {
        logger.debug('[usePushRules] Push rules updated via account data');
        // Re-read from client as it maintains the parsed rules
        setPushRules(client.pushRules);
      }
    };

    // Subscribe to account data changes
    client.on(ClientEvent.AccountData, handleAccountData);

    // Cleanup subscription on unmount or client change
    return () => {
      logger.debug('[usePushRules] Cleaning up account data listener');
      client.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [client]);

  /**
   * Get the current notification state for a room
   *
   * @param {string} roomId - The Matrix room ID
   * @returns {string} The current PushRuleState value (NOTIFY, MENTIONS_ONLY, or DONT_NOTIFY)
   */
  const getRoomState = useCallback((roomId) => {
    return getRoomPushRuleState(client, roomId);
    // Note: pushRules in deps ensures this callback returns fresh data
    // when push rules change, even though it's not directly used
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, pushRules]);

  /**
   * Set the notification state for a room
   *
   * @param {string} roomId - The Matrix room ID
   * @param {string} newState - The desired PushRuleState value
   * @returns {Promise<string>} The new state after modification
   * @throws {Error} If the state change fails
   */
  const setRoomState = useCallback(async (roomId, newState) => {
    const currentState = getRoomPushRuleState(client, roomId);
    logger.debug('[usePushRules] Setting room state:', {
      roomId,
      currentState,
      newState,
    });
    return setRoomPushRuleState(client, roomId, newState, currentState);
  }, [client]);

  return {
    pushRules,
    getRoomState,
    setRoomState,
  };
}

// Re-export PushRuleState for convenience
export { PushRuleState };

export default usePushRules;
